import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  Typography,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ErrorIcon from '@material-ui/icons/Error';
import WarningIcon from '@material-ui/icons/Warning';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import AssessmentIcon from '@material-ui/icons/Assessment';
import SecurityIcon from '@material-ui/icons/Security';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import ScheduleIcon from '@material-ui/icons/Schedule';
import {
  AuditDashboardStats,
  AuditFinding,
  DomainAssessment,
  MaturityLevelLabel,
  AuditDomainLabel,
} from '../../types';
import { auditService } from '../../services/auditService';

const useStyles = makeStyles(theme => ({
  statCard: {
    textAlign: 'center',
    padding: theme.spacing(2),
    height: '100%',
  },
  statValue: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
  },
  statLabel: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  scoreGood: { color: theme.palette.success.main },
  scoreWarning: { color: theme.palette.warning.main },
  scoreDanger: { color: theme.palette.error.main },
  scoreInfo: { color: theme.palette.info.main },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
  },
  domainCard: {
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1.5),
  },
  domainRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  progressContainer: {
    flexGrow: 1,
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
  progressBar: {
    height: 10,
    borderRadius: 5,
  },
  maturityChip: {
    fontWeight: 'bold',
    minWidth: 90,
  },
  findingItem: {
    borderLeft: `4px solid`,
    marginBottom: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
  },
  critical: { borderLeftColor: theme.palette.error.dark },
  high: { borderLeftColor: theme.palette.error.main },
  medium: { borderLeftColor: theme.palette.warning.main },
  low: { borderLeftColor: theme.palette.info.main },
  engagementCard: {
    padding: theme.spacing(2),
    backgroundColor:
      theme.palette.type === 'dark'
        ? theme.palette.grey[800]
        : theme.palette.grey[50],
  },
  trendContainer: {
    padding: theme.spacing(2),
    height: 200,
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(1),
  },
  trendBar: {
    flex: 1,
    borderRadius: '4px 4px 0 0',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-end',
    minHeight: 20,
    transition: 'height 0.3s ease',
  },
  trendLabel: {
    fontSize: '0.65rem',
    marginTop: theme.spacing(0.5),
    textAlign: 'center',
  },
  trendValue: {
    fontSize: '0.75rem',
    fontWeight: 'bold',
    color: '#fff',
    padding: theme.spacing(0.25, 0.5),
  },
}));

const getScoreColor = (score: number, classes: any) => {
  if (score >= 70) return classes.scoreGood;
  if (score >= 50) return classes.scoreWarning;
  return classes.scoreDanger;
};

const getProgressColor = (score: number): 'primary' | 'secondary' => {
  return score >= 50 ? 'primary' : 'secondary';
};

const getSeverityIcon = (severity: string) => {
  switch (severity) {
    case 'critical':
      return <ErrorIcon style={{ color: '#d32f2f' }} />;
    case 'high':
      return <ErrorIcon style={{ color: '#f44336' }} />;
    case 'medium':
      return <WarningIcon style={{ color: '#ff9800' }} />;
    default:
      return <CheckCircleIcon style={{ color: '#2196f3' }} />;
  }
};

export const DashboardView = () => {
  const classes = useStyles();
  const [stats, setStats] = useState<AuditDashboardStats | null>(null);
  const [domains, setDomains] = useState<DomainAssessment[]>([]);
  const [findings, setFindings] = useState<AuditFinding[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      const [s, d, f] = await Promise.all([
        auditService.getDashboardStats(),
        auditService.getDomainAssessments(),
        auditService.getFindings(),
      ]);
      setStats(s);
      setDomains(d);
      setFindings(f);
      setLoading(false);
    };
    loadData();
  }, []);

  if (loading || !stats) return <LinearProgress />;

  const criticalFindings = findings
    .filter(
      f =>
        (f.severity === 'critical' || f.severity === 'high') &&
        f.status !== 'closed' &&
        f.status !== 'remediated',
    )
    .sort((a, b) => {
      const order = {
        critical: 0,
        high: 1,
        medium: 2,
        low: 3,
        informational: 4,
      };
      return (order[a.severity] ?? 4) - (order[b.severity] ?? 4);
    });

  return (
    <Box>
      {/* Engagement banner */}
      <Card className={classes.engagementCard} style={{ marginBottom: 24 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="h6">
              MoH Digital Systems Audit — WHO-Aligned Assessment v3
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Framework: WHO MAPS Toolkit &amp; Global Strategy on Digital
              Health 2020-2025
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Period: Jan 10 – Feb 10, 2026 &nbsp;|&nbsp; Lead: Dr. M. Tembo
              &nbsp;|&nbsp; Status: Under Review
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} style={{ textAlign: 'right' }}>
            <Typography variant="body2" color="textSecondary">
              Next scheduled audit
            </Typography>
            <Typography variant="h6">{stats.nextAuditDate}</Typography>
          </Grid>
        </Grid>
      </Card>

      {/* KPI Cards */}
      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        <Grid item xs={6} sm={3}>
          <Card className={classes.statCard}>
            <AssessmentIcon style={{ fontSize: 32, color: '#1976d2' }} />
            <Typography
              className={`${classes.statValue} ${getScoreColor(
                stats.overallScore,
                classes,
              )}`}
            >
              {stats.overallScore}%
            </Typography>
            <Typography className={classes.statLabel}>Overall Score</Typography>
            <Chip
              label={MaturityLevelLabel[stats.overallMaturity]}
              size="small"
              color="primary"
              variant="outlined"
              style={{ marginTop: 4 }}
            />
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card className={classes.statCard}>
            <SecurityIcon style={{ fontSize: 32, color: '#f44336' }} />
            <Typography
              className={`${classes.statValue} ${classes.scoreDanger}`}
            >
              {stats.criticalFindings}
            </Typography>
            <Typography className={classes.statLabel}>
              Critical Findings
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {stats.openFindings} open of {stats.totalFindings} total
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card className={classes.statCard}>
            <CheckCircleIcon style={{ fontSize: 32, color: '#4caf50' }} />
            <Typography className={`${classes.statValue} ${classes.scoreGood}`}>
              {stats.compliantControls}
            </Typography>
            <Typography className={classes.statLabel}>
              Compliant Controls
            </Typography>
            <Typography variant="caption" color="textSecondary">
              of {stats.totalControls} total (
              {Math.round(
                (stats.compliantControls / stats.totalControls) * 100,
              )}
              %)
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card className={classes.statCard}>
            <TrendingUpIcon style={{ fontSize: 32, color: '#ff9800' }} />
            <Typography
              className={`${classes.statValue} ${classes.scoreWarning}`}
            >
              {stats.domainsAssessed}/{stats.totalDomains}
            </Typography>
            <Typography className={classes.statLabel}>
              Domains Assessed
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {stats.overdueFindings} overdue remediation
              {stats.overdueFindings !== 1 ? 's' : ''}
            </Typography>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Domain Scores */}
        <Grid item xs={12} md={7}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Domain Maturity Scores
          </Typography>
          {domains.map(d => (
            <Card
              key={d.domain}
              className={classes.domainCard}
              variant="outlined"
            >
              <div className={classes.domainRow}>
                <Typography variant="body2" style={{ minWidth: 200 }}>
                  {AuditDomainLabel[d.domain]}
                </Typography>
                <div className={classes.progressContainer}>
                  <LinearProgress
                    variant="determinate"
                    value={d.score}
                    color={getProgressColor(d.score)}
                    className={classes.progressBar}
                  />
                </div>
                <Typography
                  variant="body2"
                  style={{
                    minWidth: 40,
                    textAlign: 'right',
                    fontWeight: 'bold',
                  }}
                >
                  {d.score}%
                </Typography>
                <Chip
                  label={`L${d.currentMaturity}`}
                  size="small"
                  className={classes.maturityChip}
                  style={{
                    marginLeft: 8,
                    backgroundColor:
                      d.currentMaturity >= 4
                        ? '#4caf50'
                        : d.currentMaturity >= 3
                        ? '#ff9800'
                        : d.currentMaturity >= 2
                        ? '#f44336'
                        : '#d32f2f',
                    color: '#fff',
                  }}
                />
              </div>
              {d.criticalFindings > 0 && (
                <Typography
                  variant="caption"
                  style={{ color: '#f44336', marginTop: 4, display: 'block' }}
                >
                  {d.criticalFindings} critical finding
                  {d.criticalFindings > 1 ? 's' : ''}
                </Typography>
              )}
            </Card>
          ))}
        </Grid>

        {/* Critical Findings + Trend */}
        <Grid item xs={12} md={5}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Critical &amp; High Findings
          </Typography>
          <Card variant="outlined" style={{ marginBottom: 24 }}>
            <List dense>
              {criticalFindings.slice(0, 8).map((f, idx) => (
                <React.Fragment key={f.id}>
                  <ListItem
                    className={`${classes.findingItem} ${
                      classes[
                        f.severity as 'critical' | 'high' | 'medium' | 'low'
                      ]
                    }`}
                  >
                    <ListItemIcon style={{ minWidth: 36 }}>
                      {getSeverityIcon(f.severity)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                          [{f.id}] {f.title}
                        </Typography>
                      }
                      secondary={
                        <Box
                          display="flex"
                          alignItems="center"
                          style={{ gap: 8, marginTop: 2 }}
                        >
                          <Chip
                            label={f.severity}
                            size="small"
                            style={{
                              backgroundColor:
                                f.severity === 'critical'
                                  ? '#d32f2f'
                                  : '#f44336',
                              color: '#fff',
                              height: 20,
                              fontSize: '0.7rem',
                            }}
                          />
                          <Chip
                            label={f.status}
                            size="small"
                            variant="outlined"
                            style={{ height: 20, fontSize: '0.7rem' }}
                          />
                          <Typography variant="caption" color="textSecondary">
                            <ScheduleIcon
                              style={{ fontSize: 12, verticalAlign: 'middle' }}
                            />{' '}
                            {f.targetDate}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  {idx < criticalFindings.length - 1 && (
                    <Divider variant="inset" component="li" />
                  )}
                </React.Fragment>
              ))}
            </List>
          </Card>

          {/* Score Trend */}
          <Typography variant="h6" className={classes.sectionTitle}>
            Score Trend
          </Typography>
          <Card variant="outlined">
            <div className={classes.trendContainer}>
              {stats.trendData.map(t => (
                <div
                  key={t.period}
                  className={classes.trendBar}
                  style={{
                    height: `${t.overallScore * 1.8}px`,
                    backgroundColor:
                      t.overallScore >= 50
                        ? '#4caf50'
                        : t.overallScore >= 35
                        ? '#ff9800'
                        : '#f44336',
                  }}
                >
                  <span className={classes.trendValue}>{t.overallScore}</span>
                </div>
              ))}
            </div>
            <Box display="flex" justifyContent="space-around" pb={1}>
              {stats.trendData.map(t => (
                <Typography key={t.period} className={classes.trendLabel}>
                  {t.period}
                </Typography>
              ))}
            </Box>
          </Card>

          {/* Compliance breakdown mini */}
          <Card variant="outlined" style={{ marginTop: 24, padding: 16 }}>
            <Typography variant="subtitle2" gutterBottom>
              Control Compliance
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={4}>
                <Typography
                  variant="h5"
                  style={{ color: '#4caf50', fontWeight: 'bold' }}
                >
                  {stats.compliantControls}
                </Typography>
                <Typography variant="caption">Compliant</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography
                  variant="h5"
                  style={{ color: '#ff9800', fontWeight: 'bold' }}
                >
                  {stats.partialControls}
                </Typography>
                <Typography variant="caption">Partial</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography
                  variant="h5"
                  style={{ color: '#f44336', fontWeight: 'bold' }}
                >
                  {stats.nonCompliantControls}
                </Typography>
                <Typography variant="caption">Non-Compliant</Typography>
              </Grid>
            </Grid>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
